import { computed, ref, watch } from "vue";
import { cloneObject, debounce, deepEqual, scrollToSelector } from "@spoferan/spoferan-ts-core";
import { useApiFetch } from "./useApiFetch.mjs";
import { useNuxtApp, useRouter, useRoute, useI18n } from "#imports";
import { useOnline } from "@vueuse/core";
export const DataLoaderComponentPropDefaults = {
  data: [],
  persistFilterKeys: [],
  filter: {},
  params: {},
  loading: true,
  permissions: {}
};
export const DataLoaderComponentEmits = ["loaded", "filtering", "updated", "update:data", "update:filter", "update:total", "update:loading", "update:permissions"];
export function useDataLoaderComponent(dataLoaderElement, props, emit, filter = void 0) {
  const route = useRoute();
  const router = useRouter();
  const isOnline = useOnline();
  const { $apiFetch } = useNuxtApp();
  const { t } = useI18n();
  const filterData = filter ?? ref(props.filter);
  watch(() => props.filter, (data) => {
    filterData.value = data;
  }, { deep: true });
  watch(filterData, (newValue) => {
    emit("update:filter", newValue);
  });
  const isLoading = ref(props.loading);
  watch(isLoading, (loading) => {
    emit("update:loading", loading);
  });
  const loadedData = ref(props.data);
  watch(loadedData, (data) => {
    emit("update:data", data);
  });
  const loadedPermissions = ref(props.permissions);
  watch(loadedPermissions, (permissions) => {
    emit("update:permissions", permissions);
  });
  watch(isOnline, (newValue) => {
    if (newValue) {
      reload(void 0, true, false);
    }
  });
  const reservedRequestProps = {
    page: "page",
    // Controls the requested page, i.e., the offset of the result set
    limit: "limit",
    // Controls how many results shall be retrieved per page
    order: "order",
    // Defines the props on which the results shall be ordered
    dir: "dir"
    // Defines the direction of the order props
  };
  const hasMore = ref(false);
  const availableResults = ref(props.total ?? props.data.length ?? 0);
  watch(availableResults, (total) => {
    emit("update:total", total);
  });
  const fromPosition = ref(0);
  const toPosition = ref(0);
  const error = ref(null);
  const page = ref(props.persistFilterKeys.includes(reservedRequestProps.page) ? parseInt(route.query[reservedRequestProps.page]) : 1);
  const replaceData = ref(true);
  let isLoaded = false;
  const filterExists = computed(() => {
    return Object.keys(filterData.value).filter((key) => !Object.values(reservedRequestProps).includes(key) && !Object.values(props.passiveFilterKeys ?? []).includes(key) && !!filterData.value[key]).length > 0;
  });
  const requestData = computed(() => {
    return {
      ...props.params,
      ...cloneObject(filterData.value),
      // Set the pagination type
      [props.simplePaginated ? "simple_paginated" : "paginated"]: true
    };
  });
  const nonPersistFilterKeyValueMapping = {};
  props.persistFilterKeys.map((key) => {
    const filterValue = filterData.value[key] ?? null;
    if (filterValue && route.query[key] !== filterValue) {
      nonPersistFilterKeyValueMapping[key] = filterValue;
    }
  });
  if (!props.preventUrlWatcher) {
    watch(() => props.url, () => {
      reset();
    });
  }
  watch(requestData, (newData, oldData) => {
    if (deepEqual(newData, oldData)) {
      return;
    }
    const immediateLoadMore = !newData || !newData.search || oldData && (oldData.search === newData.search || Math.abs((oldData.search ? oldData.search.length : 0) - newData.search.length) > 2);
    if (immediateLoadMore) {
      applyFilter();
    } else {
      applyDebounceFilter();
    }
  }, { deep: true });
  async function reload(targetPage = void 0, showLoader = true, focus = !props.noLoadFocus) {
    replaceData.value = true;
    page.value = targetPage ?? page.value;
    return load(showLoader, true).then(() => {
      if (focus) {
        scrollToDataLoaderElement();
      }
    });
  }
  async function reset(showLoader = !props.noLoadFocus) {
    filterData.value = {};
    page.value = 1;
    return load(showLoader).then(() => {
      if (showLoader) {
        scrollToDataLoaderElement();
      }
    });
  }
  async function applyFilter() {
    page.value = 1;
    saveFilterToQueryParams();
    emit("filtering");
    return load();
  }
  const applyDebounceFilter = debounce(applyFilter, 500);
  async function load(showLoader = true, byPassCache = false) {
    error.value = null;
    isLoading.value = showLoader;
    const fetchParams = {
      ...requestData.value,
      // We explicitly set the page when requesting a data load whereby we do not automatically add it to the request data
      [reservedRequestProps.page]: page.value
    };
    if (byPassCache) {
      fetchParams["ts"] = Math.round(Date.now() / 1e3);
    }
    const fetchOptions = {
      params: fetchParams,
      guest: props.asGuest,
      // Prevent error redirect and only display internal error message in the component as a data loader component is only nested on a page
      lazyError: true
    };
    try {
      let response;
      if (props.lazy || props.auth) {
        response = await $apiFetch(props.url, fetchOptions);
      } else {
        const result = await useApiFetch(props.url, fetchOptions);
        if (result.error.value) {
          setError(result.error.value);
          return;
        }
        response = result.response;
      }
      const data = response.data;
      hasMore.value = !!response.links?.next;
      availableResults.value = response.meta?.total ?? data.length;
      fromPosition.value = response.meta?.from ?? (data.length ? 1 : 0);
      toPosition.value = response.meta?.to ?? data.length;
      if (response.permissions) {
        loadedPermissions.value = response.permissions;
      }
      if (replaceData.value) {
        if (props.onScroll && page.value !== 1) {
          const limit = requestData.value[reservedRequestProps.limit] ?? data.length;
          loadedData.value.splice((page.value - 1) * limit, limit, ...data);
        } else {
          loadedData.value = data;
        }
      } else {
        loadedData.value.push(...data);
      }
      const wasInitialLoad = !isLoaded;
      isLoaded = true;
      handleLoadedData(wasInitialLoad);
      if (wasInitialLoad) {
        emit("loaded", data);
      }
      emit("updated", response);
    } catch (requestError) {
      if (!isOnline.value) {
        setError({
          statusCode: 503,
          message: t("error.offline.message"),
          retryable: true
        });
      } else {
        const statusCode = requestError?.statusCode ?? 500;
        setError({
          statusCode,
          message: props.errorMessage ?? requestError.message,
          retryable: ![403, 404].includes(statusCode)
        });
      }
    }
    isLoading.value = false;
    replaceData.value = true;
  }
  function setError(error2) {
    error2.value = error2;
    isLoading.value = false;
    replaceData.value = true;
  }
  async function loadPrevious(replace = false) {
    if (page.value > 1 && !isLoading.value) {
      replaceData.value = replace;
      page.value--;
      saveFilterToQueryParams();
      return load().then(() => {
        if (replace && !props.noLoadFocus) {
          scrollToDataLoaderElement();
        }
      });
    }
  }
  async function loadNext(replace = false) {
    if (hasMore.value && !isLoading.value) {
      replaceData.value = replace;
      page.value++;
      saveFilterToQueryParams();
      return load().then(() => {
        if (replace && !props.noLoadFocus) {
          scrollToDataLoaderElement();
        }
      });
    }
  }
  function scrollToDataLoaderElement() {
    scrollToSelector(dataLoaderElement.value);
  }
  function saveFilterToQueryParams() {
    if (props.persistFilterKeys.length) {
      const queryParams = { ...route.query };
      props.persistFilterKeys.forEach((key) => {
        const filterValue = filterData.value[key];
        if (!["", null, void 0].includes(filterValue) && (!Array.isArray(filterValue) || filterValue.length) && (!nonPersistFilterKeyValueMapping[key] || nonPersistFilterKeyValueMapping[key] !== filterValue)) {
          queryParams[key] = filterValue;
        } else {
          delete queryParams[key];
        }
      });
      router.replace({ query: queryParams }).catch(() => {
      });
    }
  }
  function handleLoadedData(isFirstLoad) {
  }
  function onIntersect(isIntersecting) {
    if (props.onScroll && !error.value && isIntersecting) {
      loadNext();
    }
  }
  return {
    loadedData,
    isLoading,
    loadNext,
    loadPrevious,
    load,
    reload,
    reset,
    hasMore,
    filterExists,
    onIntersect,
    replaceData,
    availableResults,
    fromPosition,
    toPosition,
    page,
    error
  };
}
